import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { scoreTextFromWinner } from "../lib/scoreFromWinner";
import { Link } from "react-router-dom";

import { routes } from "../routes";

import { formatDateTime, formatDateWithTime } from "../lib/dateFormat";
import AmigosPanel from "../components/AmigosPanel";
import PlayerFamilyProfileSwitcher from "../components/PlayerFamilyProfileSwitcher";
import PlayerRetoActions from "../components/PlayerRetoActions";
import PlayerFamilyTip from "../components/PlayerFamilyTip";
import { useAuth } from "../auth/AuthContext";
import {
  api,
  ApiError,
  type DashboardMatchBrief,
  type H2HPayload,
  type PlayerDashboardPayload,
  type PlayerPaymentOrder,
  type PlayerPendingDocumentsItem,
} from "../lib/api";
import { PLAYER_DOCUMENTS_ANCHOR } from "../components/PlayerPendingDocumentsCard";
import { drawResultsPath } from "../lib/drawResultsLink";
import { makeLatestOnly } from "../lib/latestOnly";
import { usePlayerProfileScope } from "../lib/usePlayerProfileScope";
import { formatMoney } from "../lib/money";
import { PLAYER_STAGE_NEXT_MATCH_MESSAGE, PLAYER_STAGE_RESULTS_MESSAGE, playerStageLocked } from "../lib/playerStage";
import { isSchedulePendingForPlayer, SCHEDULE_PENDING_LABEL } from "../lib/matchScheduleStatus";
import { courtNoticeName } from "../lib/courts";
import { jornadaRangeText } from "../lib/jornadaDates";
import { retoOwnSide, retoReportHref, retoRival, retoShowsContact } from "../lib/retoContacto";
import {
  nextMatchNoteParts,
  nextMatchWindow,
  nextMatchWindowParts,
  restRoundLine,
  roundBadge,
} from "./PlayerTournamentHomePage";
import Icon from "../components/icons";
import { orderPaymentHref } from "../lib/orderPaymentLink";

function isBye(match: DashboardMatchBrief, profileId?: number): boolean {
  const status = match.status?.toLowerCase();
  if (status === "bye" || match.score_text?.trim().toUpperCase() === "BYE") {
    return true;
  }
  if (profileId && match.player1?.player_profile_id === profileId) {
    return !match.player2;
  }
  if (profileId && match.player2?.player_profile_id === profileId) {
    return !match.player1;
  }
  return Boolean(match.player1 && !match.player2);
}

function capitalizeFirst(text: string): string {
  return text ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : text;
}

function opponentName(match: DashboardMatchBrief, profileId?: number): string {
  if (isBye(match, profileId)) return "BYE";
  // Con el lado del jugador (dobles: los dos lados vienen sin perfil).
  if (retoOwnSide(match, { profileId })) {
    return retoRival(match, { profileId })?.display_name ?? "Por definir";
  }
  return match.player1?.display_name ?? match.player2?.display_name ?? "Por definir";
}

function isPlayoffMatch(match: DashboardMatchBrief): boolean {
  return /playoff/i.test(match.notes ?? "");
}

function directPlayoffSeedLabel(match: DashboardMatchBrief): string | null {
  const matchDirectSeed = (match.notes ?? "").match(/Lugar directo\s+([12])/i);
  if (!matchDirectSeed) return null;
  return matchDirectSeed[1] === "1" ? "1er" : "2do";
}

function localTodayIso(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

/** Título del reto según el formato (Eduardo 2026-10-07, torneo 67 en round
 * robin): «Hoy juegas por subir.» es de la escalera y solo ahí sale. En los
 * demás formatos, «Hoy juegas.» solo si el partido tiene fecha de hoy; si
 * no, «Tu próximo partido.». */
export function nextChallengeHeadline(match: DashboardMatchBrief, today: string = localTodayIso()): string {
  if (isPlayoffMatch(match)) return "¡Felicidades!";
  if (isBye(match)) return "Esta semana tienes BYE.";
  if (match.format_type === "ladder") return "Hoy juegas por subir.";
  if (match.scheduled_date && match.scheduled_date.slice(0, 10) === today) return "Hoy juegas.";
  return "Tu próximo partido.";
}

function nextChallengeMessage(match: DashboardMatchBrief, profileId?: number): string | null {
  const playoff = isPlayoffMatch(match);
  const directSeed = directPlayoffSeedLabel(match);
  if (playoff && isBye(match, profileId) && directSeed) {
    return `Pasaste a Playoffs y además descansas por ser el ${directSeed} lugar de tu categoría.`;
  }
  if (playoff) {
    return "Pasaste a Playoffs.";
  }
  return null;
}

/** Cuándo se juega el reto, junto al nombre del torneo. Eduardo 2026-10-05
 * (torneo 67): sin calendario de canchas nadie «confirma» una fecha; va la
 * jornada con sus fechas, igual que «Tu próximo reto» del inicio del torneo
 * («Jornada 1 · del martes 6 al domingo 11 de octubre», ya sin «juega antes
 * del domingo»), o solo la jornada si el organizador no puso fechas. Con
 * calendario, como siempre. */
function nextChallengeWhen(match: DashboardMatchBrief, profileId?: number): string {
  const pending = isSchedulePendingForPlayer(match);
  if (match.uses_court_calendar === false && !pending && !match.scheduled_date) {
    // Un BYE no tiene nada que jugar: solo la jornada.
    return (isBye(match, profileId) ? null : nextMatchWindow(match)) ?? matchLabel(match);
  }
  return formatDateWithTime(
    match.scheduled_date,
    match.scheduled_time,
    pending ? SCHEDULE_PENDING_LABEL : "Fecha por confirmar",
  );
}

/** Columna «Fecha» de la tabla: sin calendario de canchas, las fechas de la
 * jornada en lugar de «Por confirmar» (Eduardo 2026-10-05). Sin fechas de
 * jornada, o en un BYE, «—»: nadie confirma nada y la jornada ya va en
 * «Partido». */
function upcomingDateLabel(match: DashboardMatchBrief, profileId?: number): string {
  if (match.uses_court_calendar === false && !match.scheduled_date) {
    const range =
      !isBye(match, profileId) && match.round_starts_on && match.round_ends_on
        ? jornadaRangeText(match.round_starts_on, match.round_ends_on)
        : null;
    if (range) return range;
    if (!isSchedulePendingForPlayer(match)) return "—";
  }
  return formatDateWithTime(
    match.scheduled_date,
    match.scheduled_time,
    isSchedulePendingForPlayer(match) ? SCHEDULE_PENDING_LABEL : "Por confirmar",
  );
}

function opponentParticipant(match: DashboardMatchBrief, profileId?: number) {
  return retoRival(match, { profileId });
}

function matchLabel(match: DashboardMatchBrief): string {
  // round_display_label: etiqueta canónica cuando la ronda es de playoffs
  // ("Jornada N" en BD) — la deriva el backend, display-only.
  return match.round_display_label ?? match.round_label ?? (match.match_number ? `Partido ${match.match_number}` : "Reto");
}

function matchPlayersLabel(match: DashboardMatchBrief): ReactNode {
  const player1 = match.player1?.display_name ?? "Por definir";
  const player2 = isBye(match) ? "BYE" : match.player2?.display_name ?? "Por definir";
  // Eduardo 2026-10-06: el ganador va en negritas.
  const p1Won = dashboardWinnerIsPlayer1(match);
  return (
    <>
      {p1Won === true ? <strong>{player1}</strong> : player1}
      {" vs "}
      {p1Won === false ? <strong>{player2}</strong> : player2}
    </>
  );
}

/** ¿Ganó el jugador 1? null sin ganador (el dashboard trae participant_id). */
function dashboardWinnerIsPlayer1(match: DashboardMatchBrief): boolean | null {
  const winner = match.winner_participant_id;
  if (winner == null) return null;
  if (match.player1?.participant_id === winner) return true;
  if (match.player2?.participant_id === winner) return false;
  return null;
}

/** Eduardo 2026-10-06: el marcador se muestra siempre desde quien ganó. */
function dashboardScoreFromWinner(match: DashboardMatchBrief): string {
  return scoreTextFromWinner(match.score_text, dashboardWinnerIsPlayer1(match)).trim();
}

function scoreLabel(match: DashboardMatchBrief): string {
  return dashboardScoreFromWinner(match) || "Resultado capturado";
}

function resultPerspective(match: DashboardMatchBrief, profileId?: number): "win" | "loss" | "other" {
  if (!profileId || !match.winner_participant_id) return "other";
  if (match.player1?.player_profile_id === profileId) {
    return match.winner_participant_id === match.player1.participant_id ? "win" : "loss";
  }
  if (match.player2?.player_profile_id === profileId) {
    return match.winner_participant_id === match.player2.participant_id ? "win" : "loss";
  }
  return "other";
}

function normalizeRoundLabel(label: string | null | undefined): string {
  return (label ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

/** Etiqueta efectiva para reconocer etapas: los playoffs de RR/escalera traen
 * round_label "Jornada N" y la etapa canónica derivada en round_display_label. */
function effectiveRoundLabel(match: DashboardMatchBrief): string | null {
  return match.round_display_label ?? match.round_label ?? null;
}

function isFinalStageMatch(match: DashboardMatchBrief): boolean {
  const roundLabel = normalizeRoundLabel(effectiveRoundLabel(match));
  // Only the championship final grants champion/runner-up recognition.
  // `Cuartos de final`, `Octavos de final`, and `Semifinales` are not the
  // tournament final; `Tercer lugar` is its own 3rd-place recognition.
  return roundLabel === "final";
}

function isThirdPlaceMatch(match: DashboardMatchBrief): boolean {
  const roundLabel = normalizeRoundLabel(effectiveRoundLabel(match));
  return /tercer\s+lugar|3er\s+lugar|tercero/.test(roundLabel);
}

function isKnockoutResult(match: DashboardMatchBrief): boolean {
  const roundLabel = normalizeRoundLabel(effectiveRoundLabel(match));
  return isPlayoffMatch(match)
    || isFinalStageMatch(match)
    || isThirdPlaceMatch(match)
    || /dieciseis|16\s*avos|octavos|8\s*avos|cuartos|4\s*tos|semifinal/.test(roundLabel);
}

function bestRankProgress(tournaments: PlayerDashboardPayload["tournaments"]) {
  return [...tournaments]
    .filter((t) => typeof t.rank_delta === "number" && t.rank_delta > 0)
    .sort((a, b) => (b.rank_delta ?? -999) - (a.rank_delta ?? -999))[0] ?? null;
}

function h2hCopy(h2h: H2HPayload | null, opponentNameValue?: string | null): string {
  if (!h2h || h2h.summary.total_matches === 0) {
    return opponentNameValue ? `Primer capítulo vs ${opponentNameValue}.` : "Historial listo cuando haya rival.";
  }
  return `Historial vs ${h2h.opponent.display_name}: ${h2h.summary.player_wins}–${h2h.summary.opponent_wins}`;
}

function MatchTableEmpty({ children }: { children: string }) {
  return <p className="player-home-match-empty muted">{children}</p>;
}

function UpcomingMatchesTable({
  matches,
  profileId,
  emptyMessage = "No tienes partidos próximos en tus torneos activos.",
}: {
  matches: DashboardMatchBrief[];
  profileId?: number;
  emptyMessage?: string;
}) {
  if (matches.length === 0) {
    return <MatchTableEmpty>{emptyMessage}</MatchTableEmpty>;
  }

  return (
    <div className="player-home-match-table-wrap" role="region" aria-label="Tabla de próximos partidos">
      <table className="player-home-match-table">
        <thead>
          <tr>
            <th>Torneo</th>
            <th>Partido</th>
            <th>Rival</th>
            <th>Fecha</th>
            <th>Cancha</th>
            <th>Acción</th>
          </tr>
        </thead>
        <tbody>
          {matches.map((match) => {
            const opponent = opponentParticipant(match, profileId);
            const opponentDisplay = opponentName(match, profileId);
            return (
              <tr key={match.id}>
                <td>{match.tournament_name ?? "Torneo"}</td>
                <td>{matchLabel(match)}</td>
                <td>
                  {opponent?.player_profile_id ? (
                    <Link className="player-opponent-profile-link" to={routes.playerGlobalProfile(opponent.player_profile_id)}>
                      {opponentDisplay}
                    </Link>
                  ) : (
                    opponentDisplay
                  )}
                </td>
                <td>{upcomingDateLabel(match, profileId)}</td>
                <td>{match.court || "—"}</td>
                <td>
                  {isBye(match, profileId) ? (
                    <span className="player-home-match-note">Descanso</span>
                  ) : match.allow_player_result_reporting ? (
                    <Link className="player-home-match-action" to={retoReportHref(match.id, profileId)}>
                      Reportar
                    </Link>
                  ) : (
                    <span className="player-home-match-note">Admin</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function RecentResultsTable({
  matches,
  emptyMessage = "Aún no hay resultados recientes en tus torneos.",
}: {
  matches: DashboardMatchBrief[];
  emptyMessage?: string;
}) {
  if (matches.length === 0) {
    return <MatchTableEmpty>{emptyMessage}</MatchTableEmpty>;
  }

  return (
    <div className="player-home-match-table-wrap" role="region" aria-label="Tabla de resultados recientes">
      <table className="player-home-match-table player-home-match-table--results">
        <thead>
          <tr>
            <th>Torneo</th>
            <th>Partido</th>
            <th>Jugadores</th>
            <th>Resultado</th>
          </tr>
        </thead>
        <tbody>
          {matches.map((match) => (
            <tr key={match.id}>
              <td>{match.tournament_name ?? "Torneo"}</td>
              <td>{matchLabel(match)}</td>
              <td>{matchPlayersLabel(match)}</td>
              <td>{scoreLabel(match)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function pendingDocumentLabels(item: PlayerPendingDocumentsItem): string {
  return item.pending.map((field) => field.label).join(" · ");
}

/** Home del torneo con la tarjeta «Documentos pendientes» a la vista. */
function pendingDocumentsLink(item: PlayerPendingDocumentsItem): string {
  return `/player/tournaments/${item.tournament_id}#${PLAYER_DOCUMENTS_ANCHOR}`;
}

export default function HomeJugadorPage() {
  const { token, user } = useAuth();
  const [data, setData] = useState<PlayerDashboardPayload | null>(null);
  // Perfil activo de la cuenta familiar: una sola fuente para todas las
  // pantallas (Eduardo 2026-09-29).
  const scope = usePlayerProfileScope();
  const activeProfileId = scope.profileId;
  const latestLoad = useMemo(() => makeLatestOnly(), []);
  const latestUnread = useMemo(() => makeLatestOnly(), []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unreadCount, setUnreadCount] = useState<number | null>(null);
  const [nextOpponentH2h, setNextOpponentH2h] = useState<H2HPayload | null>(null);
  const [clubCount, setClubCount] = useState(0);
  const [paymentOrders, setPaymentOrders] = useState<PlayerPaymentOrder[]>([]);
  // Documentos de inscripción pendientes de TODA la cuenta (cada hijo, por torneo).
  const [pendingDocuments, setPendingDocuments] = useState<PlayerPendingDocumentsItem[]>([]);

  const load = useCallback(async () => {
    if (!token || !scope.ready) return;
    const attempt = latestLoad.begin();
    setLoading(true);
    setError(null);
    try {
      const response = await api.dashboards.player(token, activeProfileId);
      if (!latestLoad.isCurrent(attempt)) return;
      setData(response.data);
    } catch (err) {
      if (!latestLoad.isCurrent(attempt)) return;
      if (err instanceof ApiError) {
        setError(err.message || "No pudimos cargar tu home.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      if (latestLoad.isCurrent(attempt)) setLoading(false);
    }
  }, [activeProfileId, latestLoad, scope.ready, token]);

  // Avisos sin leer del hijo elegido (incluye los de toda la cuenta).
  const loadUnread = useCallback(async () => {
    if (!token || !scope.ready) return;
    const attempt = latestUnread.begin();
    try {
      const response = await api.notifications.listForPlayer(token, {
        limit: 1,
        player_profile_id: activeProfileId,
      });
      if (latestUnread.isCurrent(attempt)) setUnreadCount(response.data.unread_count);
    } catch {
      if (latestUnread.isCurrent(attempt)) setUnreadCount(null);
    }
  }, [activeProfileId, latestUnread, scope.ready, token]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void loadUnread();
  }, [loadUnread]);

  useEffect(() => {
    let alive = true;
    if (!token) return;
    (async () => {
      try {
        const response = await api.dashboards.playerPaymentOrders(token);
        if (alive) setPaymentOrders(response.data.orders);
      } catch {
        // sin deudas visibles ante error: la sección simplemente no aparece
      }
    })();
    return () => {
      alive = false;
    };
  }, [token]);

  useEffect(() => {
    let alive = true;
    if (!token) return;
    (async () => {
      try {
        const response = await api.player.pendingRegistrationDocuments(token);
        // Eduardo 2026-09-30: el aviso del home solo sale cuando el organizador
        // ya pidió los documentos (foto y CURP vienen obligatorias por omisión
        // en todos los torneos); la tarjeta dentro del torneo sale siempre.
        if (alive) setPendingDocuments((response.data.items ?? []).filter((item) => item.organizer_reminded === true));
      } catch {
        // sin aviso ante error: la tarjeta del torneo sigue siendo la fuente
      }
    })();
    return () => {
      alive = false;
    };
  }, [token]);

  useEffect(() => {
    if (!token || !scope.ready) return;
    let alive = true;
    api.clubs
      .playerClubs(token, activeProfileId)
      .then((resp) => {
        if (alive) setClubCount(resp.data.clubs.length);
      })
      .catch(() => {
        if (alive) setClubCount(0);
      });
    return () => {
      alive = false;
    };
  }, [activeProfileId, scope.ready, token]);

  const profile = data?.profile;
  // Rank FMT y WTN en el home (pedido de Eduardo 2026-07-15).
  const fmtRanking = profile?.official_rankings?.[0] ?? null;
  const hasRankChips = Boolean(fmtRanking || profile?.wtn_rating != null);
  const familyProfiles = scope.profiles;
  const selectedProfileId = profile?.id ?? activeProfileId;
  const handleProfileChange = (nextId: number) => {
    setNextOpponentH2h(null);
    scope.setProfileId(nextId);
  };
  // Aviso «Te faltan documentos en …»: lo del hijo que se está viendo y, en
  // una cuenta familiar, una pista de los hermanos que también deben algo.
  // Se espera a saber qué perfil se ve para no atribuirle lo de otro.
  const documentsProfileId = scope.ready ? activeProfileId ?? profile?.id ?? null : null;
  const ownPendingDocuments = scope.ready
    ? pendingDocuments.filter(
        (item) => documentsProfileId === null || item.player_profile_id === documentsProfileId,
      )
    : [];
  const siblingPendingDocuments =
    scope.ready && documentsProfileId !== null
      ? pendingDocuments.filter(
          (item) =>
            item.player_profile_id !== documentsProfileId &&
            familyProfiles.some((option) => option.id === item.player_profile_id),
        )
      : [];
  const summary = data?.summary;
  const upcomingMatches = data?.upcoming_matches ?? [];
  // Eduardo 2026-10-05: inscripciones que descansan la jornada actual (el
  // servidor solo lo manda sin calendario de canchas, con el torneo abierto
  // y ya armado). Con varios torneos o categorías, un reto es de la
  // inscripción que descansa si coincide el torneo y, cuando viene, la
  // inscripción desde la que lo ve.
  const restingCards = (data?.tournaments ?? []).filter(
    (card) => Boolean(card.rest_round) && !playerStageLocked(card.player_visibility),
  );
  const restCardFor = (match: DashboardMatchBrief) =>
    restingCards.find(
      (card) =>
        card.tournament_id === match.tournament_id &&
        (match.viewer_participant_id == null || match.viewer_participant_id === card.participant_id),
    ) ?? null;
  // El reto de arriba: el primero que sí se juega esta jornada. El servidor
  // ordena por fecha y luego por id (con 5 o más retos los del torneo más
  // antiguo salen primero), así que el de la siguiente jornada de donde
  // descansa podía tapar el de esta semana en otro torneo. Solo si todos son
  // de inscripciones que descansan, el primero de la lista.
  const nextMatch = upcomingMatches.find((match) => !restCardFor(match)) ?? upcomingMatches[0] ?? null;
  const recentResults = data?.recent_results ?? [];
  const recentCommunication = data?.recent_communications[0] ?? null;
  const recentResult = recentResults[0] ?? null;
  // Torneo que el organizador sigue armando (candado del jugador): el home
  // explica por qué no hay partidos ni resultados en vez de «sin partidos».
  const lockedTournament = (data?.tournaments ?? []).find(
    (tournament) => playerStageLocked(tournament.player_visibility),
  ) ?? null;
  // Con varios torneos, el aviso dice CUÁL se está armando.
  const lockedPrefix = lockedTournament?.tournament_name ? `${lockedTournament.tournament_name}: ` : "";
  const pendingRuleTournament = (data?.tournaments ?? []).find(
    (tournament) =>
      tournament.rule_acceptance?.rule_published &&
      !tournament.rule_acceptance.accepted,
  );
  const rulesLink = pendingRuleTournament
    ? `/player/tournaments/${pendingRuleTournament.tournament_id}/rules`
    : "/player/tournaments";
  const nextMatchOpponent = nextMatch ? opponentParticipant(nextMatch, profile?.id) : null;
  const progressTournament = useMemo(
    () => bestRankProgress(data?.tournaments ?? []),
    [data?.tournaments],
  );
  const recentResultTone = recentResult ? resultPerspective(recentResult, profile?.id) : "other";
  // Eduardo 2026-10-05: un premio de OTRO torneo no tapa el reto pendiente.
  // Con «Últimos resultados» por fecha jugada, la Semifinal perdida de junio
  // (Escalera 2026) entraba aquí y «Gracias por participar» escondía el reto
  // de otro torneo, sus botones y «Último resultado». El premio es para
  // cuando ya no hay reto («prepárate para el siguiente reto»).
  const awardFitsHero = (match: DashboardMatchBrief) =>
    !nextMatch || nextMatch.tournament_id === match.tournament_id;
  const finalAwardMatch = recentResults.find((match) => {
    const perspective = resultPerspective(match, profile?.id);
    return awardFitsHero(match) && isFinalStageMatch(match) && (perspective === "win" || perspective === "loss");
  }) ?? null;
  const thirdPlaceAwardMatch = recentResults.find((match) => {
    const perspective = resultPerspective(match, profile?.id);
    return awardFitsHero(match) && isThirdPlaceMatch(match) && perspective === "win";
  }) ?? null;
  const participationMatch = recentResults.find((match) => {
    const perspective = resultPerspective(match, profile?.id);
    const hasUpcomingInTournament = upcomingMatches.some((upcoming) => upcoming.tournament_id === match.tournament_id);
    return awardFitsHero(match)
      && !finalAwardMatch
      && !thirdPlaceAwardMatch
      && !hasUpcomingInTournament
      && isKnockoutResult(match)
      && perspective === "loss";
  }) ?? null;
  const playoffAwardMatch = finalAwardMatch ?? thirdPlaceAwardMatch ?? participationMatch;
  const playoffAwardTone: "champion" | "runner-up" | "third-place" | "participation" | "other" = finalAwardMatch
    ? resultPerspective(finalAwardMatch, profile?.id) === "win" ? "champion" : "runner-up"
    : thirdPlaceAwardMatch
      ? "third-place"
      : participationMatch
        ? "participation"
        : "other";
  const playoffAwardCopy = playoffAwardMatch
    ? playoffAwardTone === "champion"
      ? {
          title: "¡Campeón!",
          badge: "🏆 Campeón",
          icon: "🏆",
          cardLabel: "Campeón de categoría",
          message: "Cerraste el torneo en lo más alto. Disfrútalo: este trofeo es resultado de constancia, partidos ganados y mentalidad competitiva.",
        }
      : playoffAwardTone === "runner-up"
        ? {
            title: "¡Subcampeón!",
            badge: "🥈 2do lugar",
            icon: "🥈",
            cardLabel: "Finalista · 2do lugar",
            message: "Llegaste hasta la final. Ese segundo lugar también habla de carácter: recarga, aprende y vuelve por el campeonato.",
          }
        : playoffAwardTone === "third-place"
          ? {
              title: "¡Tercer lugar!",
              badge: "🥉 3er lugar",
              icon: "🥉",
              cardLabel: "3er lugar de categoría",
              message: "Cerraste el torneo en el podio. Felicidades por competir hasta el final y ganar el tercer lugar.",
            }
          : {
              title: "Gracias por participar",
              badge: "👏 Participación",
              icon: "👏",
              cardLabel: "Participación completada",
              message: "Gracias por competir y cerrar el torneo. Revisa tus resultados y prepárate para el siguiente reto.",
            }
    : null;
  const isMatchBye = nextMatch ? isBye(nextMatch, profile?.id) : false;
  // Mismas acciones que el inicio del torneo: sin calendario de canchas
  // siempre el contacto del rival (Eduardo 2026-10-03).
  const showRetoActions = !isMatchBye && retoShowsContact(nextMatch);
  const retoWindow = nextMatchWindowParts(nextMatch);
  const retoNote = nextMatchNoteParts(nextMatch, profile?.id);
  // Eduardo 2026-10-05: si descansa la jornada actual, el home lo dice igual
  // que el inicio del torneo y debajo va su siguiente reto (antes presentaba
  // la jornada siguiente como si tocara ya). Solo cuando el reto de arriba es
  // de esa misma inscripción (ya se prefirió el de una que sí juega). Sin
  // reto publicado, el de cualquier torneo donde descanse. Un BYE ya
  // publicado conserva su propio mensaje; con el torneo cerrado o en armado
  // el servidor no manda descanso.
  const restCard = nextMatch
    ? isMatchBye
      ? null
      : restCardFor(nextMatch)
    : restingCards[0] ?? null;
  const restRound = restCard?.rest_round ?? null;
  const restLine = restRoundLine(restRound);

  useEffect(() => {
    const opponentProfileId = nextMatchOpponent?.player_profile_id;
    if (!token || !opponentProfileId || isMatchBye) {
      setNextOpponentH2h(null);
      return;
    }
    let cancelled = false;
    api.player.h2h(token, opponentProfileId, profile?.id)
      .then((response) => {
        if (!cancelled) setNextOpponentH2h(response.data);
      })
      .catch(() => {
        if (!cancelled) setNextOpponentH2h(null);
      });
    return () => {
      cancelled = true;
    };
  }, [token, profile?.id, nextMatchOpponent?.player_profile_id, isMatchBye]);

  return (
    <>
      <section className="player-pro-home" aria-label="Home de jugador">
        <div className="player-pro-home__topbar">
          <div>
            <span className="player-pro-kicker">Torneos123</span>
            <strong>{profile ? `Hola, ${profile.first_name}` : "Home jugador"}</strong>
          </div>
          <div className="player-family-switcher-shell">
            <PlayerFamilyProfileSwitcher
              familyProfiles={familyProfiles}
              activeProfileId={selectedProfileId}
              onChange={handleProfileChange}
            />
            <Link className="player-pro-avatar" to="/player/profile" aria-label="Abrir perfil">
              {profile?.first_name?.charAt(0).toUpperCase() ?? "J"}
            </Link>
          </div>
        </div>
        {/* Cuenta Familiar (Eduardo 2026-10-01): una sola vez, señala «Jugando como». */}
        <PlayerFamilyTip userId={user?.id} count={familyProfiles.length} />

        <div className="player-pro-hero">
          {hasRankChips && (
            <Link className="player-pro-hero__ranks" to="/player/profile" aria-label="Tu ranking">
              {fmtRanking && (
                <span className="player-pro-rank-chip">
                  <small>Rank FMT</small>
                  <strong>#{fmtRanking.position}</strong>
                  <em>{fmtRanking.category_name}</em>
                </span>
              )}
              {profile?.wtn_rating != null && (
                <span className="player-pro-rank-chip">
                  <small>WTN</small>
                  <strong>{profile.wtn_rating.toFixed(1)}</strong>
                  {profile.wtn_rating_doubles != null && <em>Dobles {profile.wtn_rating_doubles.toFixed(1)}</em>}
                </span>
              )}
            </Link>
          )}
          <span className="player-pro-eyebrow">⚡ Próximo reto</span>
          {loading ? (
            <>
              <h1>Cargando tu siguiente juego…</h1>
              <p>Estamos buscando rival, jornada y cancha.</p>
            </>
          ) : playoffAwardCopy && playoffAwardMatch ? (
            <>
              <span className={`player-pro-award-badge player-pro-award-badge--${playoffAwardTone}`}>
                {playoffAwardCopy.badge}
              </span>
              <h1>{playoffAwardCopy.title}</h1>
              <p>
                <strong className="player-pro-hero__message">{playoffAwardCopy.message}</strong>
                <br />
                {playoffAwardMatch.tournament_name ? `${playoffAwardMatch.tournament_name} · ` : ""}
                {playoffAwardMatch.round_display_label ?? playoffAwardMatch.round_label ?? "Final"}
                {playoffAwardMatch.score_text ? ` · ${dashboardScoreFromWinner(playoffAwardMatch)}` : ""}
              </p>
              <div className="player-pro-award-card" aria-label="Reconocimiento de torneo">
                <span className="player-pro-award-card__trophy" aria-hidden="true">
                  {playoffAwardCopy.icon}
                </span>
                <div>
                  <span>{playoffAwardCopy.cardLabel}</span>
                  <strong>{profile?.display_name ?? profile?.first_name ?? "Jugador"}</strong>
                  <small>{playoffAwardMatch.tournament_name ?? "Torneos123"}</small>
                </div>
              </div>
            </>
          ) : nextMatch ? (
            <>
              {/* Descansa esta jornada: no es «Hoy juegas»; el duelo de abajo
                  es su siguiente reto. */}
              <h1>{restRound ? "Descansas esta jornada." : nextChallengeHeadline(nextMatch)}</h1>
              <p>
                {nextChallengeMessage(nextMatch, profile?.id) ? (
                  <>
                    <strong className="player-pro-hero__message">{nextChallengeMessage(nextMatch, profile?.id)}</strong>
                    <br />
                  </>
                ) : null}
                {nextMatch.tournament_name ? `${nextMatch.tournament_name} · ` : ""}
                {restRound ? (
                  <>
                    {restLine ?? restRound.round_label}
                    <br />
                    {`Tu siguiente reto · ${nextChallengeWhen(nextMatch, profile?.id)}`}
                  </>
                ) : (
                  nextChallengeWhen(nextMatch, profile?.id)
                )}
                {courtNoticeName(nextMatch.court) ? ` · ${courtNoticeName(nextMatch.court)}` : ""}
              </p>
              {/* Tarjeta del reto con el formato del inicio del torneo
                  (Eduardo 2026-10-06): jornada, fechas, «Coordina directo
                  con tu rival», WhatsApp / Llamar / Reportar resultado y
                  «Ir al torneo». */}
              <section className="pt-reto-theme pt-home__reto player-home-reto" aria-label="Próximo partido">
                <div className="pt-home__reto-head">
                  <span className="pt-home__eyebrow">{restRound ? "Tu siguiente reto" : "Tu próximo reto"}</span>
                  <span className="pt-home__pill">{roundBadge(nextMatch)}</span>
                </div>
                <div className="pt-home__reto-who">
                  <h2 className="pt-home__rival">
                    {nextMatchOpponent?.player_profile_id ? (
                      <Link
                        className="pt-home__rival-link"
                        to={routes.playerGlobalProfile(nextMatchOpponent.player_profile_id)}
                      >
                        {isMatchBye ? opponentName(nextMatch, profile?.id) : `vs ${opponentName(nextMatch, profile?.id)}`}
                      </Link>
                    ) : isMatchBye ? (
                      opponentName(nextMatch, profile?.id)
                    ) : (
                      `vs ${opponentName(nextMatch, profile?.id)}`
                    )}
                  </h2>
                  {retoWindow ? (
                    <p className="pt-home__dates" data-no-toast data-jornada-window>
                      {capitalizeFirst(retoWindow.range)}
                    </p>
                  ) : null}
                  {nextMatch.tournament_name ? (
                    <p className="player-home-reto__tournament" data-no-toast>
                      {nextMatch.tournament_name}
                    </p>
                  ) : null}
                </div>
                {retoNote.kind === "schedule" ? (
                  <p className="pt-home__reto-when" data-no-toast>
                    <Icon name="calendar" />
                    {retoNote.text}
                  </p>
                ) : null}
                {showRetoActions ? (
                  <PlayerRetoActions
                    variant="card"
                    match={nextMatch}
                    rival={nextMatchOpponent}
                    sender={profile}
                    tournamentName={nextMatch.tournament_name}
                    reportHref={retoReportHref(nextMatch.id, profile?.id)}
                    className="pt-home__reto-actions"
                    showContactLine={retoNote.kind === "contact"}
                  />
                ) : null}
                <Link className="player-home-reto__go" to={`/player/tournaments/${nextMatch.tournament_id}`}>
                  <Icon name="trophy" />
                  <span>Ir al torneo</span>
                </Link>
              </section>

              <div className="player-pro-progress-strip" aria-label="Mi progreso">
                <article>
                  <span>Mi progreso</span>
                  <strong>
                    {progressTournament?.rank_delta
                      ? `Subiste ${progressTournament.rank_delta} lugares`
                      : `${summary?.tournaments_active ?? 0} torneo(s) activo(s)`}
                  </strong>
                  <small>
                    {progressTournament?.rank_delta
                      ? `${progressTournament.initial_rank && progressTournament.current_rank ? `#${progressTournament.initial_rank} → #${progressTournament.current_rank} · ` : ""}${progressTournament.tournament_name ?? "Tu torneo"}`
                      : "Sigue sumando partidos y ranking."}
                  </small>
                </article>
                <article>
                  <span>Rivalidad</span>
                  <strong>{h2hCopy(nextOpponentH2h, nextMatchOpponent?.display_name)}</strong>
                  <small>{nextOpponentH2h?.summary.last_score ? `Último score: ${nextOpponentH2h.summary.last_score}` : "Revisa el perfil del rival."}</small>
                </article>
                <article>
                  <span>Último resultado</span>
                  <strong>
                    {recentResult
                      ? recentResultTone === "win"
                        ? "Vienes de ganar"
                        : recentResultTone === "loss"
                          ? "A recuperar terreno"
                          : "Resultado registrado"
                      : "Sin resultados aún"}
                  </strong>
                  <small>{recentResult ? scoreLabel(recentResult) : "Tu historia empieza en el próximo partido."}</small>
                </article>
              </div>

            </>
          ) : (
            <>
              <h1>{restRound ? "Descansas esta jornada." : "Listo para el próximo reto."}</h1>
              {restRound ? (
                <p>
                  {restCard?.tournament_name ? `${restCard.tournament_name} · ` : ""}
                  {restLine ?? restRound.round_label}
                  <br />
                  Tu siguiente reto aparecerá aquí cuando el organizador lo publique.
                </p>
              ) : null}
              {/* Descansa en un torneo y otro sigue en armado: van los dos
                  avisos (Eduardo 2026-10-05). */}
              {lockedTournament ? (
                <p data-no-toast>
                  {lockedPrefix}
                  {PLAYER_STAGE_NEXT_MATCH_MESSAGE}
                </p>
              ) : restRound ? null : (
                <p>
                  Cuando el admin publique tu siguiente partido, aquí tendrás rival, fecha y acciones rápidas.
                </p>
              )}
              <div className="player-pro-progress-strip" aria-label="Mi progreso">
                <article>
                  <span>Mi progreso</span>
                  <strong>{summary?.tournaments_active ?? 0} torneo(s) activo(s)</strong>
                  <small>{progressTournament?.rank_delta ? `Subiste ${progressTournament.rank_delta} lugares en ${progressTournament.tournament_name ?? "tu torneo"}` : "Mantén tu perfil al día."}</small>
                </article>
                <article>
                  <span>Último resultado</span>
                  <strong>{recentResult ? scoreLabel(recentResult) : lockedTournament ? "Sin resultados publicados" : "Sin resultados aún"}</strong>
                  <small>
                    {recentResult
                      ? recentResult.tournament_name ?? "Resultado reciente"
                      : lockedTournament
                        ? PLAYER_STAGE_RESULTS_MESSAGE
                        : "Tu historia empieza en el próximo partido."}
                  </small>
                </article>
              </div>
            </>
          )}

        </div>
      </section>

      {(ownPendingDocuments.length > 0 || siblingPendingDocuments.length > 0) && (
        <section className="player-home-documents" aria-label="Documentos pendientes" data-no-toast>
          <span className="player-pro-eyebrow">Documentos pendientes</span>
          {ownPendingDocuments.map((item) => (
            <article
              key={`${item.player_profile_id}-${item.tournament_id}`}
              className="player-home-documents__item"
              role="status"
            >
              <div className="player-home-documents__copy">
                <strong>Te faltan documentos en {item.tournament_name ?? "tu torneo"}</strong>
                <small>{pendingDocumentLabels(item)}</small>
              </div>
              {/* Con varios torneos, la lista de ligas de un lector de
                  pantalla no debe tener dos «Subir documentos» iguales. */}
              <Link
                className="button"
                to={pendingDocumentsLink(item)}
                aria-label={`Subir documentos de ${item.tournament_name ?? "tu torneo"}`}
              >
                Subir documentos
              </Link>
            </article>
          ))}
          {siblingPendingDocuments.map((item) => {
            const sibling = familyProfiles.find((option) => option.id === item.player_profile_id);
            const siblingName = sibling?.first_name?.trim() || item.player_name || "Otro perfil";
            return (
              <article
                key={`${item.player_profile_id}-${item.tournament_id}`}
                className="player-home-documents__item player-home-documents__item--sibling"
                role="status"
              >
                <div className="player-home-documents__copy">
                  <strong>{siblingName} también tiene documentos pendientes</strong>
                  <small>
                    {item.tournament_name ? `${item.tournament_name} · ` : ""}
                    {pendingDocumentLabels(item)}
                  </small>
                </div>
                {/* Cambia el perfil activo de la cuenta y abre su torneo. */}
                <Link
                  className="button button--secondary"
                  to={pendingDocumentsLink(item)}
                  aria-label={`Cambiar a ${siblingName}: documentos de ${item.tournament_name ?? "su torneo"}`}
                  onClick={() => handleProfileChange(item.player_profile_id)}
                >
                  Cambiar a {siblingName}
                </Link>
              </article>
            );
          })}
        </section>
      )}

      {(recentCommunication || recentResult) && (
        <section className="player-pro-feed-card">
          <span className="player-pro-eyebrow">Comunicados</span>
          {recentCommunication ? (
            <Link
              to={
                // Eduardo 2026-10-05: el aviso del sorteo abre sus resultados.
                drawResultsPath(recentCommunication.draw_results_url) ??
                (recentCommunication.tournament_id
                  ? `/player/tournaments/${recentCommunication.tournament_id}/communications`
                  : "/player/communications")
              }
            >
              <strong>{recentCommunication.title}</strong>
              <small>
                {recentCommunication.tournament_name
                  ? `${recentCommunication.tournament_name} · `
                  : ""}
                {recentCommunication.published_at
                  ? formatDateTime(recentCommunication.published_at)
                  : "Comunicado reciente"}
                {drawResultsPath(recentCommunication.draw_results_url) ? " · Ver resultados del sorteo →" : ""}
              </small>
            </Link>
          ) : recentResult ? (
            <Link to="/player/result-reports">
              <strong>Último resultado</strong>
              <small>{scoreLabel(recentResult)}</small>
            </Link>
          ) : null}
        </section>
      )}

      <section className="player-home-match-section" aria-label="Próximos partidos de todos tus torneos">
        <div className="player-pro-section-head player-home-match-section__head">
          <div>
            <span className="player-pro-eyebrow">Próximos partidos</span>
            <h2>Todos tus torneos</h2>
          </div>
        </div>
        {loading ? (
          <MatchTableEmpty>Cargando próximos partidos…</MatchTableEmpty>
        ) : (
          <UpcomingMatchesTable
            matches={upcomingMatches}
            profileId={profile?.id}
            emptyMessage={lockedTournament ? `${lockedPrefix}${PLAYER_STAGE_NEXT_MATCH_MESSAGE}` : undefined}
          />
        )}
        {/* Botones grandes al pie (Eduardo 2026-10-06): antes eran ligas
            tenues en la cabecera de la sección. */}
        <Link className="player-home-cta" to="/player/tournaments">
          <span className="player-home-cta__icon" aria-hidden="true">
            <Icon name="trophy" />
          </span>
          <span className="player-home-cta__text">
            <strong>Ver torneos</strong>
            <small>Tus torneos y los que tienen inscripciones abiertas.</small>
          </span>
          <span className="player-home-cta__arrow" aria-hidden="true">›</span>
        </Link>
      </section>

      <section className="player-home-match-section" aria-label="Resultados recientes de todos tus torneos">
        <div className="player-pro-section-head player-home-match-section__head">
          <div>
            <span className="player-pro-eyebrow">Resultados</span>
            <h2>Últimos Resultados</h2>
          </div>
        </div>
        {loading ? (
          <MatchTableEmpty>Cargando resultados…</MatchTableEmpty>
        ) : (
          <RecentResultsTable
            matches={recentResults}
            emptyMessage={lockedTournament ? `${lockedPrefix}${PLAYER_STAGE_RESULTS_MESSAGE}` : undefined}
          />
        )}
        <Link className="player-home-cta" to="/player/profile/results">
          <span className="player-home-cta__icon" aria-hidden="true">
            <Icon name="award" />
          </span>
          <span className="player-home-cta__text">
            <strong>Ver resultados históricos</strong>
            <small>Todos tus partidos jugados, torneo por torneo.</small>
          </span>
          <span className="player-home-cta__arrow" aria-hidden="true">›</span>
        </Link>
      </section>

      {paymentOrders.length > 0 && (
        <section className="player-home-match-section" aria-label="Pagos pendientes">
          <div className="player-pro-section-head player-home-match-section__head">
            <div>
              <span className="player-pro-eyebrow">Pagos pendientes</span>
              <h2>Inscripciones por pagar</h2>
            </div>
          </div>
          {paymentOrders.map((order) => (
            <article
              key={order.order_id}
              className="card"
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.75rem", marginBottom: "0.6rem" }}
            >
              <div>
                <strong>{order.tournament_name}</strong>
                {familyProfiles.length > 1 && order.player_name && (
                  <span className="muted"> · {order.player_name}</span>
                )}
                <div className="muted">{formatMoney(order.gross_amount, order.currency)}</div>
              </div>
              <Link className="button" to={orderPaymentHref(order)}>
                Pagar
              </Link>
            </article>
          ))}
        </section>
      )}

      <section className="player-pro-status-grid" aria-label="Accesos rápidos pendientes">
        <Link className="player-pro-mini-card player-pro-mini-card--action" to="/player/tournaments">
          <span>Torneos activos</span>
          <strong>{summary?.tournaments_active ?? 0}</strong>
          <small>Ver detalles en Torneos.</small>
        </Link>
        <Link className="player-pro-mini-card player-pro-mini-card--action" to="/player/notifications">
          <span>Avisos</span>
          <strong>{unreadCount ?? 0}</strong>
          <small>{(unreadCount ?? 0) > 0 ? "Abrir avisos sin leer" : "Ver centro de avisos"}</small>
        </Link>
        <Link className="player-pro-mini-card player-pro-mini-card--action" to={rulesLink}>
          <span>Reglamentos</span>
          <strong>{summary?.pending_rule_acceptances ?? 0}</strong>
          <small>{(summary?.pending_rule_acceptances ?? 0) > 0 ? "Aceptar pendiente" : "Ver reglamentos"}</small>
        </Link>
        {clubCount > 0 && (
          <Link className="player-pro-mini-card player-pro-mini-card--action" to="/player/clubs">
            <span>Mi Club</span>
            <strong>🎾</strong>
            <small>Reservar cancha</small>
          </Link>
        )}
      </section>

      {error && (
        <section className="card">
          <p role="alert" className="form-error">
            {error}
          </p>
        </section>
      )}

      {/* Amigos */}
      <AmigosPanel myProfileId={profile?.id} />

    </>
  );
}
